import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Header,
  SpaceBetween,
  Container,
  Spinner,
  Link,
  Button,
  Table,
  StatusIndicator,
  Tabs,
  Modal,
  Box,
  Grid,
  Pagination,
  CollectionPreferences,
  TextFilter,
} from '@amzn/awsui-components-react';
import KeyValuePairs from '@amzn/awsui-components-react/polaris/key-value-pairs';
import CopyToClipboard from '@amzn/awsui-components-react/polaris/copy-to-clipboard';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { API, Auth } from 'aws-amplify';
import { getSurveyDetailsAction } from '../redux/getSurveyDetails';
import { SURVEY_TYPE } from '../../../../common/components/pdf_generator/templates/SurveyPDFTemplate/utils/DataUtils';
import { ROUTE_PATH } from '../../../../common/constants/route_paths';
import {
  VALARI_API_NAME,
  GET_PROJECT_DOCUMENTS,
  GET_AUDIT_ENDPOINT,
} from '../../../../common/config/api_endpoints';
import { DEFAULT_BUSINESS_ID, DEFAULT_COUNTRY } from '../../../../common/config/api_constants';
import Page from '../../../../common/components/with_page/Page';
import { BRC_ITEMS_PAGE } from '../../../../common/components/with_page/components/bread_crumb/constants';
import { RESOURCE } from '../../../auth/constants/rbac_constants';
import { CURRENT_STUDY_PERIOD, getStudyPeriod } from '../../../../common/constants/study_period';
import { CustomDataRenderer, getSectionHeading } from './utils/SurveyDetailsPageUtil';

const formatTimestamp = currentStateId => {
  if (!currentStateId) return 'N/A';
  const timestampPart = currentStateId.split('#')[1];
  if (!timestampPart) return 'N/A';

  try {
    const date = new Date(timestampPart);
    return date.toLocaleString();
  } catch (error) {
    return 'N/A';
  }
};

const extractAlias = email => {
  if (!email) return 'N/A';
  return email.split('@')[0];
};

const createPhoneToolLink = alias => {
  if (!alias || alias === 'N/A') return alias;
  return (
    <Link href={`https://phonetool.amazon.com/users/${alias}`} external>
      {alias}
    </Link>
  );
};

const getAuditTableData = auditTrail => {
  if (!auditTrail || !Array.isArray(auditTrail)) return [];

  return auditTrail.map((item, index) => {
    const getEventDescription = changes => {
      if (!changes || typeof changes !== 'object') return '';

      const descriptions = [];

      if (changes.userResponse) {
        Object.keys(changes.userResponse).forEach(key => {
          const field = changes.userResponse[key];
          if (field && field.description) {
            if (key === 'submit') return;
            if (key === 'nextActionMetadata') return;
            if (key === 'nextAction') return;

            if (key === 'substantiationProjects') {
              const items =
                field.to?.substantiationProjectItems || field.from?.substantiationProjectItems;
              if (!items || items.length === 0) return;
            }

            if (key === 'substantiationProjectDetails') {
              const items =
                field.to?.substantiationProjectDetailsItems ||
                field.from?.substantiationProjectDetailsItems;
              if (!items || items.length === 0) return;
            }

            if (key === 'projectEmployees') {
              const data = field.to?.data || field.from?.data;
              if (!data || data.length === 0) return;
            }

            descriptions.push(field.description);
          }
        });
      }

      Object.keys(changes).forEach(key => {
        if (key !== 'userResponse' && key !== 'lastUpdatedBy' && key !== 'secondaryAssignees') {
          const field = changes[key];
          if (field && field.description) {
            descriptions.push(field.description);
          }
        }
      });

      const hasActiveStep = changes.userResponse?.activeStep !== undefined;

      // Don't add "User saved the survey" for "Survey Created" action
      if (!hasActiveStep && item.action !== 'Survey Created') {
        descriptions.push('User saved the survey');
      }

      return descriptions.join('; ');
    };

    const getUserInfo = changes => {
      const email = changes?.lastUpdatedBy;
      if (!email) return null;

      const alias = extractAlias(email);
      return createPhoneToolLink(alias);
    };

    return {
      id: index,
      time: formatTimestamp(item.currentStateId),
      user: getUserInfo(item.changes),
      event: item.action || 'N/A',
      eventDescription: getEventDescription(item.changes),
      changes: item.changes || {},
      rawItem: item,
    };
  });
};

const formatDate = isoDate => {
  if (!isoDate) return 'N/A';
  return new Date(isoDate).toLocaleDateString();
};

const makeCopyable = value => {
  if (!value || value === 'N/A') return value;
  return (
    <CopyToClipboard
      copyButtonText="Copy"
      copyErrorText="Failed to copy"
      copySuccessText="Copied"
      textToCopy={value}
      variant="inline"
    />
  );
};

const renderStatus = status => {
  switch (status) {
    case 'Created':
      return <StatusIndicator type="info">Notification sent</StatusIndicator>;
    case 'InProgress':
      return <StatusIndicator type="in-progress">Work in progress</StatusIndicator>;
    case 'Submitted':
      return <StatusIndicator type="success">Completed</StatusIndicator>;
    case 'error':
      return <StatusIndicator type="error">Error fetching status</StatusIndicator>;
    default:
      return <StatusIndicator type="info">Survey not created</StatusIndicator>;
  }
};

const getDetailsItems = (details, surveyType, spoDetails) => {
  const baseItems = [
    { label: 'Survey ID', value: makeCopyable(get(details, 'surveyId', 'N/A')) },
    { label: 'Survey Type', value: surveyType || 'N/A' },
    { label: 'Survey Status', value: renderStatus(get(details, 'surveyStatus', 'N/A')) },
    { label: 'Due Date', value: formatDate(get(details, 'surveyEndDate', 'N/A')) },
    {
      label: 'Business Component ID',
      value: makeCopyable(get(details, 'businessComponentId', 'N/A')),
    },
    { label: 'Last Updated On', value: formatDate(get(details, 'lastUpdatedOn')) },
    {
      label: 'Last Updated By',
      value: createPhoneToolLink(extractAlias(get(details, 'lastUpdatedBy'))),
    },
  ];

  if (surveyType === SURVEY_TYPE.LABOR_BC_SUBSTANTIATION) {
    const spoAlias = get(details, 'userAlias');
    return [
      { label: 'SPO Alias', value: createPhoneToolLink(spoAlias) },
      { label: 'SPO Name', value: get(details, 'userName', 'N/A') },
      ...baseItems,
    ];
  }

  if (surveyType === SURVEY_TYPE.LABOR_PROJECT_SUBSTANTIATION) {
    const spAlias = get(details, 'userAlias');
    const spoAlias = get(spoDetails, 'userAlias');
    const spoSurveyId = get(details, 'spoSurveyId');
    return [
      { label: 'SP Alias', value: createPhoneToolLink(spAlias) },
      { label: 'SP Name', value: get(details, 'userName', 'N/A') },
      ...baseItems,
      { label: 'SPO Survey ID', value: makeCopyable(spoSurveyId) },
      { label: 'SPO Alias', value: createPhoneToolLink(spoAlias) },
    ];
  }

  return baseItems;
};

const getDocumentationTableData = (details, projectDocuments) => {
  const projectId = get(details, 'projectId');
  if (!projectId || !projectDocuments[projectId]) {
    return [];
  }

  return projectDocuments[projectId].map(doc => ({
    documentName: get(doc, 'documentFileName', 'N/A'),
    type: get(doc, 'documentSubCategory', 'N/A'),
    presignedUrl: get(doc, 'presignedURL', ''),
    documentId: get(doc, 'projectDocumentId', ''),
  }));
};

const getSPSurveysTableData = details => {
  try {
    const userResponse = JSON.parse(get(details, 'userResponse', '{}'));
    const substantiationProjectDetails = get(
      userResponse,
      'SubstantiationProjectDetails.substantiationProjectDetailsItems',
      [],
    );

    return substantiationProjectDetails.map(item => {
      const providerId = get(item, 'substantiationProviderId', '');
      const spAlias =
        providerId
          .split('_')
          .pop()
          ?.split('@')[0] || 'N/A';

      return {
        projectName: get(item, 'projectName', 'N/A'),
        substantiationProvider: get(item, 'substantiationPrimaryOwner', 'N/A'),
        spAlias,
        status: get(item, 'substantiationProjectSurveyStatus', 'N/A'),
        surveyId: get(item, 'surveyId', ''),
      };
    });
  } catch (error) {
    console.error('Error parsing userResponse for SP surveys:', error);
    return [];
  }
};

const SurveyDetailsPage = ({
  getSurveyDetails,
  surveyDetails,
  isFetching,
  entityId,
  studyPeriod,
  studyType,
}) => {
  const [spoDetails, setSpoDetails] = useState(null);
  const [loadingSpo, setLoadingSpo] = useState(false);
  const [spoFetched, setSpoFetched] = useState(false);
  const [projectDocuments, setProjectDocuments] = useState({});
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentsFetched, setDocumentsFetched] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditFetched, setAuditFetched] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAuditItem, setSelectedAuditItem] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterQuery, setFilterQuery] = useState('');
  const [spSurveysData, setSpSurveysData] = useState([]);
  const [loadingSPSurveys, setLoadingSPSurveys] = useState(false);
  const loadingSPSurveysRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const surveyId = params.get('surveyId');

    if (surveyId) {
      getSurveyDetails({
        body: { surveyId },
      });
    }
  }, [getSurveyDetails]);

  const fetchSPSurveysData = useCallback(async () => {
    if (loadingSPSurveysRef.current) return;

    loadingSPSurveysRef.current = true;
    setLoadingSPSurveys(true);

    try {
      const details = get(surveyDetails, 'body', {});
      const initialData = getSPSurveysTableData(details);
      if (initialData.length === 0) {
        setSpSurveysData([]);
        return;
      }

      const updatedData = await Promise.all(
        initialData.map(async survey => {
          if (survey.surveyId) {
            try {
              const accessToken = (await Auth.currentSession()).getAccessToken().getJwtToken();
              const clientId = DEFAULT_BUSINESS_ID + (entityId || '');

              const response = await API.post(VALARI_API_NAME, '/getSurveyDetails', {
                body: {
                  surveyId: survey.surveyId,
                  accessToken,
                  clientId,
                  country: DEFAULT_COUNTRY,
                  studyPeriod,
                },
              });

              const latestStatus = get(response, 'body.surveyStatus', survey.status);
              return {
                ...survey,
                status: latestStatus,
              };
            } catch (error) {
              console.error(`Error fetching status for survey ${survey.surveyId}:`, error);
              return survey;
            }
          }
          return survey;
        }),
      );

      setSpSurveysData(updatedData);
    } catch (error) {
      console.error('Error fetching SP surveys data:', error);
      const details = get(surveyDetails, 'body', {});
      setSpSurveysData(getSPSurveysTableData(details));
    } finally {
      loadingSPSurveysRef.current = false;
      setLoadingSPSurveys(false);
    }
  }, [surveyDetails, entityId, studyPeriod]);

  useEffect(() => {
    const fetchSpoDetails = async () => {
      const details = get(surveyDetails, 'body', {});
      const surveyType = get(details, 'surveyType');
      const year = get(details, 'year');
      const spoSurveyId = get(details, 'spoSurveyId');

      if (
        surveyType === SURVEY_TYPE.LABOR_PROJECT_SUBSTANTIATION &&
        spoSurveyId &&
        spoSurveyId.trim() !== '' &&
        year &&
        !spoFetched
      ) {
        setSpoFetched(true);
        setLoadingSpo(true);

        try {
          const accessToken = (await Auth.currentSession()).getAccessToken().getJwtToken();
          const clientId = DEFAULT_BUSINESS_ID + (entityId || '');

          const response = await API.post(VALARI_API_NAME, '/getSurveyDetails', {
            body: {
              surveyId: spoSurveyId,
              accessToken,
              clientId,
              country: DEFAULT_COUNTRY,
              studyPeriod: year,
            },
          });

          setSpoDetails(get(response, 'body', {}));
        } catch (error) {
          console.error('Error fetching SPO details:', error);
        } finally {
          setLoadingSpo(false);
        }
      }
    };

    const fetchAuditData = async () => {
      const details = get(surveyDetails, 'body', {});
      const surveyId = get(details, 'surveyId');

      if (!surveyId || auditFetched) return;

      setLoadingAudit(true);

      try {
        const accessToken = (await Auth.currentSession()).getAccessToken().getJwtToken();
        const clientId = DEFAULT_BUSINESS_ID + (entityId || '');

        const fetchAllPages = async (accumulatedData = [], nextToken = null) => {
          const requestBody = {
            entityType: 'SurveyAudit',
            entityId: surveyId,
            accessToken,
            clientId,
            country: DEFAULT_COUNTRY,
            studyPeriod,
          };

          if (nextToken) {
            requestBody.nextToken = nextToken;
          }

          const response = await API.post(VALARI_API_NAME, GET_AUDIT_ENDPOINT, {
            body: requestBody,
          });

          const auditTrail = JSON.parse(get(response, 'body.auditTrail', '[]'));
          const newAccumulatedData = [...accumulatedData, ...auditTrail];

          const responseNextToken = get(response, 'body.nextToken');
          const hasMore = get(response, 'body.hasMore', false);

          if (hasMore && responseNextToken) {
            return fetchAllPages(newAccumulatedData, responseNextToken);
          }

          return newAccumulatedData;
        };

        const allAuditTrail = await fetchAllPages();

        setAuditData({
          auditTrail: allAuditTrail,
          nextToken: null,
          hasMore: false,
        });

        setAuditFetched(true); // Set flag only after successful fetch
      } catch (error) {
        console.error('Error fetching audit data:', error);
      } finally {
        setLoadingAudit(false);
      }
    };

    fetchSpoDetails();
    fetchAuditData();
    fetchSPSurveysData();
  }, [surveyDetails, spoFetched, entityId, auditFetched, studyPeriod, fetchSPSurveysData]);

  useEffect(() => {
    const fetchProjectDocuments = async () => {
      const details = get(surveyDetails, 'body', {});
      const surveyType = get(details, 'surveyType');
      const projectId = get(details, 'projectId');
      const surveyId = get(details, 'surveyId');

      if (
        surveyType === SURVEY_TYPE.LABOR_PROJECT_SUBSTANTIATION &&
        projectId &&
        surveyId &&
        !documentsFetched
      ) {
        setDocumentsFetched(true);
        setLoadingDocuments(true);

        try {
          const accessToken = (await Auth.currentSession()).getAccessToken().getJwtToken();
          const clientId = DEFAULT_BUSINESS_ID + (entityId || '');

          const response = await API.post(VALARI_API_NAME, GET_PROJECT_DOCUMENTS, {
            body: {
              projectIdList: [projectId],
              surveyId,
              accessToken,
              clientId,
              country: DEFAULT_COUNTRY,
              studyPeriod: get(details, 'year', CURRENT_STUDY_PERIOD),
            },
          });

          const documentMap = JSON.parse(get(response, 'body.projectIdToDocumentDataMap', '{}'));
          setProjectDocuments(documentMap);
        } catch (error) {
          console.error('Error fetching project documents:', error);
        } finally {
          setLoadingDocuments(false);
        }
      }
    };

    fetchProjectDocuments();
  }, [surveyDetails, entityId, documentsFetched]);

  const details = get(surveyDetails, 'body', {});
  const surveyType = get(details, 'surveyType', '');
  const userName = get(details, 'userName', '');
  const surveyId = get(details, 'surveyId', '');
  const year = get(details, 'year', '');

  const getHeaderTitle = () => {
    return userName ? `${surveyType} Survey - ${userName}` : `${surveyType} Survey`;
  };

  const getViewLink = (targetSurveyId, targetYear) => {
    if (!targetSurveyId || !targetYear) return null;
    const rndSurveyDashboardEndpoint = `/${targetYear}${ROUTE_PATH.RND_SURVEY_DASHBOARD_PAGE}`;
    const encodedSurveyId = encodeURIComponent(targetSurveyId);
    return `${process.env.REACT_APP_SME_EXPERIENCE_URI +
      rndSurveyDashboardEndpoint}?surveyId=${encodedSurveyId}`;
  };

  const renderDocumentationTable = () => {
    const documentData = getDocumentationTableData(details, projectDocuments);

    return (
      <Container header={<Header variant="h2">Documentation</Header>}>
        <Table
          columnDefinitions={[
            {
              id: 'documentName',
              header: 'Document Name',
              cell: item =>
                item.presignedUrl ? (
                  <Link href={item.presignedUrl} external>
                    {item.documentName}
                  </Link>
                ) : (
                  item.documentName
                ),
            },
            {
              id: 'type',
              header: 'Type',
              cell: item => item.type,
            },
          ]}
          items={documentData}
          empty="No documents available"
          variant="embedded"
        />
      </Container>
    );
  };

  const renderDiffModal = () => {
    if (!selectedAuditItem || !selectedAuditItem.changes) return null;

    const { changes } = selectedAuditItem;

    const renderSection = (sectionKey, section) => {
      if (!section || typeof section !== 'object') return null;

      // Handle userResponse which contains nested changes
      if (sectionKey === 'userResponse' && section) {
        return Object.keys(section)
          .filter(subKey => {
            if (subKey === 'submit') return false;
            if (subKey === 'nextActionMetadata') return false;
            if (subKey === 'nextAction') return false;

            const subSection = section[subKey];
            if (!subSection || typeof subSection !== 'object') return false;

            // Skip if substantiationProjects has empty or null items
            if (subKey === 'substantiationProjects') {
              const items =
                subSection.to?.substantiationProjectItems ||
                subSection.from?.substantiationProjectItems;
              if (!items || items.length === 0) return false;
            }

            // Skip if substantiationProjectDetails has empty or null items
            if (subKey === 'substantiationProjectDetails') {
              const items =
                subSection.to?.substantiationProjectDetailsItems ||
                subSection.from?.substantiationProjectDetailsItems;
              if (!items || items.length === 0) return false;
            }

            // Skip if projectEmployees has empty data array
            if (subKey === 'projectEmployees') {
              const data = subSection.to?.data || subSection.from?.data;
              if (!data || data.length === 0) return false;
            }

            return true;
          })
          .map(subKey => {
            const subSection = section[subKey];

            return (
              <Container
                key={`${sectionKey}.${subKey}`}
                header={<Header variant="h3">{getSectionHeading(sectionKey, subKey)}</Header>}
              >
                <Grid
                  gridDefinition={[
                    { colspan: { default: 12, xxs: 6 } },
                    { colspan: { default: 12, xxs: 6 } },
                  ]}
                >
                  <Box padding="m">
                    <Header variant="h4">Before</Header>
                    <Box variant="code">
                      <CustomDataRenderer
                        sectionKey={sectionKey}
                        subKey={subKey}
                        data={subSection.from}
                        surveyId={surveyId}
                        entityId={entityId}
                        studyPeriod={studyPeriod}
                      />
                    </Box>
                  </Box>
                  <Box padding="m">
                    <Header variant="h4">After</Header>
                    <Box variant="code">
                      <CustomDataRenderer
                        sectionKey={sectionKey}
                        subKey={subKey}
                        data={subSection.to}
                        surveyId={surveyId}
                        entityId={entityId}
                        studyPeriod={studyPeriod}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Container>
            );
          });
      }

      // Handle other sections (like status)
      return (
        <Container
          key={sectionKey}
          header={<Header variant="h3">{getSectionHeading(sectionKey)}</Header>}
        >
          <Grid
            gridDefinition={[
              { colspan: { default: 12, xxs: 6 } },
              { colspan: { default: 12, xxs: 6 } },
            ]}
          >
            <Box padding="m">
              <Header variant="h4">Before</Header>
              <Box variant="code">
                <CustomDataRenderer
                  sectionKey={sectionKey}
                  subKey={null}
                  data={section.from}
                  surveyId={surveyId}
                  entityId={entityId}
                  studyPeriod={studyPeriod}
                />
              </Box>
            </Box>
            <Box padding="m">
              <Header variant="h4">After</Header>
              <Box variant="code">
                <CustomDataRenderer
                  sectionKey={sectionKey}
                  subKey={null}
                  data={section.to}
                  surveyId={surveyId}
                  entityId={entityId}
                  studyPeriod={studyPeriod}
                />
              </Box>
            </Box>
          </Grid>
        </Container>
      );
    };

    return (
      <Modal
        onDismiss={() => setModalVisible(false)}
        visible={modalVisible}
        header="Audit Details"
        size="max"
      >
        <Box padding="s" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <SpaceBetween size="l">
            {Object.keys(changes)
              .filter(sectionKey => sectionKey !== 'secondaryAssignees')
              .map(sectionKey => renderSection(sectionKey, changes[sectionKey]))}
          </SpaceBetween>
        </Box>
      </Modal>
    );
  };

  const renderAuditTabs = () => {
    const auditTableData = getAuditTableData(auditData?.auditTrail);

    const startIndex = (currentPageIndex - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const filteredData =
      filterQuery === ''
        ? auditTableData
        : auditTableData.filter(item => {
            const searchTerm = filterQuery.toLowerCase();

            return (
              (item.time &&
                String(item.time)
                  .toLowerCase()
                  .includes(searchTerm)) ||
              (item.user &&
                String(item.user)
                  .toLowerCase()
                  .includes(searchTerm)) ||
              (item.event &&
                String(item.event)
                  .toLowerCase()
                  .includes(searchTerm)) ||
              (item.eventDescription &&
                String(item.eventDescription)
                  .toLowerCase()
                  .includes(searchTerm)) ||
              (item.changes &&
                Object.keys(item.changes).some(key =>
                  String(item.changes[key])
                    .toLowerCase()
                    .includes(searchTerm),
                ))
            );
          });
    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const paginatedData = filteredData.slice(startIndex, endIndex);
    const tabs = [
      {
        label: 'Survey Audits',
        id: 'survey-audits',
        content: (
          <Table
            columnDefinitions={[
              {
                id: 'time',
                header: 'Time',
                cell: item => item.time,
              },
              {
                id: 'user',
                header: 'User',
                cell: item => item.user || '-',
              },
              {
                id: 'event',
                header: 'Event',
                cell: item => item.event,
              },
              {
                id: 'eventDescription',
                header: 'Event Description',
                cell: item => {
                  if (!item.eventDescription) return '-';
                  const descriptions = item.eventDescription.split('; ');
                  return (
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {descriptions.map(desc => (
                        <li key={desc}>{desc}</li>
                      ))}
                    </ul>
                  );
                },
                width: 200,
              },
              {
                id: 'actions',
                header: 'Actions',
                cell: item => {
                  const changes = item.changes || {};
                  const hasChanges =
                    Object.keys(changes).length > 0 &&
                    Object.values(changes).some(
                      change =>
                        change && typeof change === 'object' && Object.keys(change).length > 0,
                    );
                  return hasChanges ? (
                    <Button
                      variant="link"
                      onClick={() => {
                        setSelectedAuditItem(item);
                        setModalVisible(true);
                      }}
                    >
                      View Details
                    </Button>
                  ) : (
                    '-'
                  );
                },
              },
            ]}
            items={paginatedData}
            empty="No audit records available"
            variant="embedded"
            filter={
              <TextFilter
                filteringPlaceholder="Filter audit records"
                filteringText={filterQuery}
                countText={`${filteredData.length} matches`}
                onChange={event => {
                  setFilterQuery(event.detail.filteringText);
                  setCurrentPageIndex(1);
                }}
              />
            }
            preferences={
              <CollectionPreferences
                title="Preferences"
                confirmLabel="Confirm"
                cancelLabel="Cancel"
                onConfirm={event => {
                  setPageSize(event.detail.pageSize);
                  setCurrentPageIndex(1);
                }}
                preferences={{
                  pageSize,
                  contentDisplay: [
                    { id: 'variable', visible: true },
                    { id: 'value', visible: true },
                    { id: 'type', visible: true },
                    { id: 'description', visible: true },
                  ],
                }}
                pageSizePreference={{
                  title: 'Page size',
                  options: [
                    { value: 10, label: '10 records' },
                    { value: 20, label: '20 records' },
                    { value: 30, label: '30 records' },
                  ],
                }}
              />
            }
            pagination={
              <Pagination
                currentPageIndex={currentPageIndex}
                pagesCount={totalPages}
                onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
              />
            }
          />
        ),
      },
    ];

    return (
      <Container header={<Header variant="h2">Audit Information</Header>}>
        <Tabs tabs={tabs} onChange={() => {}} />
        {renderDiffModal()}
      </Container>
    );
  };

  const renderSPSurveysTable = () => {
    const tableData = spSurveysData.length > 0 ? spSurveysData : getSPSurveysTableData(details);

    return (
      <Container header={<Header variant="h2">SP Surveys</Header>}>
        <Table
          loading={loadingSPSurveys}
          columnDefinitions={[
            {
              id: 'projectName',
              header: 'SP Survey for Project',
              cell: item => {
                const itemSurveyId = item.surveyId;
                const clientId = DEFAULT_BUSINESS_ID + (entityId || '');
                const country = DEFAULT_COUNTRY;

                if (itemSurveyId) {
                  const baseUrl = `/${studyPeriod}/rise/survey/surveydetails`;
                  const params = `surveyId=${encodeURIComponent(
                    itemSurveyId,
                  )}&clientId=${encodeURIComponent(
                    clientId,
                  )}&studyPeriod=${studyPeriod}&country=${country}`;
                  const detailsUrl = `${baseUrl}?${params}`;

                  return (
                    <Link href={detailsUrl} target="_blank">
                      {item.projectName}
                    </Link>
                  );
                }
                return item.projectName;
              },
            },
            {
              id: 'spAlias',
              header: 'SP Alias',
              cell: item => createPhoneToolLink(item.spAlias),
            },
            {
              id: 'status',
              header: 'Status',
              cell: item => renderStatus(item.status),
            },
            {
              id: 'surveyId',
              header: 'Survey ID',
              cell: item => (item.status ? makeCopyable(item.surveyId) : ''),
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: item => (
                <Button
                  variant="link"
                  href={getViewLink(item.surveyId, year)}
                  target="_blank"
                  iconAlign="right"
                  iconName="external"
                  disabled={!getViewLink(item.surveyId, year) || !item.status}
                >
                  View
                </Button>
              ),
            },
          ]}
          items={tableData}
          empty="No SP surveys available"
          variant="embedded"
        />
      </Container>
    );
  };

  if (isFetching || loadingSpo || loadingDocuments || loadingAudit) {
    return (
      <Page
        PageId={RESOURCE.PAGE_RISE_SURVEY}
        isPageAccessControlled={false}
        PageContent={
          <SpaceBetween size="l">
            <Header variant="h1">Survey Details</Header>
            <Container>
              <Spinner size="large" />
            </Container>
          </SpaceBetween>
        }
        breadCrumbItems={BRC_ITEMS_PAGE.RISE_SURVEY(studyPeriod, entityId, studyType)}
      />
    );
  }

  const content = (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button
                target="_blank"
                href={getViewLink(surveyId, year)}
                iconAlign="right"
                iconName="external"
                disabled={!getViewLink(surveyId, year)}
              >
                View
              </Button>
            }
          >
            {getHeaderTitle()}
          </Header>
        }
      >
        <KeyValuePairs columns={4} items={getDetailsItems(details, surveyType, spoDetails)} />
      </Container>

      {surveyType === SURVEY_TYPE.LABOR_PROJECT_SUBSTANTIATION && renderDocumentationTable()}
      {surveyType === SURVEY_TYPE.LABOR_BC_SUBSTANTIATION && renderSPSurveysTable()}

      {renderAuditTabs()}
    </SpaceBetween>
  );

  return (
    <Page
      PageId={RESOURCE.PAGE_RISE_SURVEY}
      isPageAccessControlled={false}
      PageContent={content}
      breadCrumbItems={BRC_ITEMS_PAGE.RISE_SURVEY(studyPeriod, entityId, studyType)}
    />
  );
};

SurveyDetailsPage.propTypes = {
  getSurveyDetails: PropTypes.func.isRequired,
  surveyDetails: PropTypes.object,
  isFetching: PropTypes.bool.isRequired,
  entityId: PropTypes.string,
  studyPeriod: PropTypes.string,
  studyType: PropTypes.string,
};

const mapStateToProps = state => ({
  surveyDetails: get(state, 'entities.getSurveyDetails.data', {}),
  isFetching: get(state, 'entities.getSurveyDetails.isFetching', false),
  entityId: get(state, 'entityId.entityId'),
  studyPeriod: getStudyPeriod(state),
  studyType: get(state, 'studyType.studyType'),
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getSurveyDetails: getSurveyDetailsAction.BEGIN,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(SurveyDetailsPage);
